import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { SPREAD } from "@/constants/testIds";
import { useSoulProfile } from "@/lib/useSoulProfile";
import { houseFor } from "@/lib/houses";
import NarratedText from "@/components/NarratedText";
import { useNarrator } from "@/lib/narrator";
import { playAudioLogo } from "@/lib/audioLogo";

export default function SpreadReading() {
  const { spreadId } = useParams();
  const navigate = useNavigate();
  const [spread, setSpread] = useState(null);
  const [question, setQuestion] = useState("");
  const [reading, setReading] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [flippedIdx, setFlippedIdx] = useState(-1);
  const { element } = useSoulProfile();
  const house = houseFor(element);
  const { muted } = useNarrator();

  useEffect(() => {
    api
      .get("/oracle/spreads")
      .then((r) => setSpread(r.data.spreads.find((s) => s.id === spreadId) || null))
      .catch(() => {});
  }, [spreadId]);

  const draw = async () => {
    setDrawing(true);
    setReading(null);
    setFlippedIdx(-1);
    try {
      const { data } = await api.post("/oracle/spread", {
        spread_id: spreadId,
        question: question.trim() || null,
      });
      setReading(data);
      // Stagger flips after cards enter
      const entryTime = 400 + data.cards.length * 500 + 800;
      data.cards.forEach((_, i) => {
        setTimeout(() => {
          setFlippedIdx((cur) => Math.max(cur, i));
          playAudioLogo({ muted });
        }, entryTime + i * 1200);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The spread refused to be drawn.");
    } finally {
      setDrawing(false);
    }
  };

  const saveToJournal = async () => {
    if (!reading) return;
    try {
      await api.post("/journal/save", {
        kind: "spread",
        title: `${reading.spread_name}${reading.question ? " · " + reading.question : ""}`,
        date: reading.date,
        moon_phase: reading.moon?.phase_name,
        cards: reading.cards,
        narrative: { positions: reading.positions, thread: reading.thread },
        question: reading.question,
        topic: reading.spread_id,
      });
      setSaved(true);
      toast.success("Kept in your journal.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
  };

  if (!spread) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="font-overline breathe">Attuning…</div>
      </div>
    );
  }

  return (
    <div
      data-testid={SPREAD.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="starfield-subtle" />
      <div className="relative max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/app/oracle")}
          className="font-overline text-[#F4EFE6]/50 hover:text-[#C8A96E] mb-8"
        >
          ← Back to Oracle
        </button>

        <div className="mb-10">
          <div className="font-overline text-[#C8A96E] mb-4">
            Spread · {spread.positions} {spread.positions === 1 ? "position" : "positions"}
          </div>
          <h1 className="font-heading italic text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
            {spread.name}
          </h1>
          <p className="mt-5 font-heading text-lg text-[#F4EFE6]/70 max-w-2xl">{spread.description}</p>
        </div>

        {!reading && !drawing && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="card-frame surface-grain p-8 max-w-2xl"
          >
            <label className="font-overline block mb-3">Ask the deck (optional)</label>
            <textarea
              data-testid={SPREAD.questionInput}
              className="input-sacred min-h-[80px] resize-none"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="a question, a knot, a longing"
            />
            <div className="mt-8">
              <button data-testid={SPREAD.drawBtn} className="btn-sacred btn-sacred-solid" onClick={draw}>
                Draw the spread
              </button>
            </div>
          </motion.div>
        )}

        {drawing && (
          <div className="mt-12">
            <div className="font-heading text-2xl text-[#F4EFE6]/70 breathe">Drawing your spread…</div>
            <div className="mt-10 flex gap-6">
              {Array.from({ length: spread.positions }).map((_, i) => (
                <motion.div
                  key={i}
                  className="w-20 aspect-square card-frame rounded-2xl"
                  animate={{ opacity: [0.25, 0.85, 0.25] }}
                  transition={{ duration: 2.6, delay: i * 0.25, repeat: Infinity, ease: "easeInOut" }}
                />
              ))}
            </div>
          </div>
        )}

        {reading && (
          <div className="mt-4">
            {reading.question && (
              <div className="mb-8">
                <div className="font-overline mb-1">Your question</div>
                <div className="font-heading italic text-xl text-[#F4EFE6]/90">&ldquo;{reading.question}&rdquo;</div>
              </div>
            )}

            {/* Cards row */}
            <div
              className="grid gap-6 sm:gap-8 mb-12"
              style={{
                gridTemplateColumns: `repeat(${Math.min(reading.cards.length, 5)}, minmax(0, 1fr))`,
              }}
            >
              {reading.cards.map((c, i) => {
                const isFlipped = flippedIdx >= i;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 1.2, delay: 0.4 + i * 0.5, ease: [0.25, 0.6, 0.3, 1] }}
                    className="flex flex-col items-center"
                    style={{ perspective: "1400px" }}
                  >
                    <motion.div
                      animate={{ rotateY: isFlipped ? 180 : 0 }}
                      transition={{ duration: 1.4, ease: [0.25, 0.6, 0.3, 1] }}
                      className="relative w-full aspect-square rounded-2xl"
                      style={{
                        transformStyle: "preserve-3d",
                        boxShadow: "0 18px 48px rgba(0,0,0,0.5)",
                        willChange: "transform",
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      <div
                        className="absolute inset-0 rounded-2xl overflow-hidden"
                        style={{ backfaceVisibility: "hidden" }}
                      >
                        <img
                          src={house.back}
                          alt={`${element} House card back`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                      <div
                        className="absolute inset-0 rounded-2xl overflow-hidden bg-black"
                        style={{
                          transform: "rotateY(180deg)",
                          backfaceVisibility: "hidden",
                        }}
                      >
                        <img
                          src={mediaUrl(c.image_url)}
                          alt={c.title}
                          className="w-full h-full object-cover"
                          style={{ transform: c.upright ? "none" : "rotate(180deg)" }}
                          draggable={false}
                        />
                      </div>
                    </motion.div>
                    <div className="pt-3 text-center">
                      <div className="font-overline text-[#C8A96E]">{c.position}</div>
                      <div
                        className="font-heading italic text-sm text-[#F4EFE6] mt-1 leading-tight transition-opacity duration-700"
                        style={{ opacity: isFlipped ? 1 : 0.35 }}
                      >
                        {c.title}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Position readings */}
            <div className="space-y-8 mb-12">
              {reading.positions.map((p, i) => (
                <motion.div
                  key={i}
                  data-testid={SPREAD.position(i)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.2, delay: 1 + reading.cards.length * 0.6 + i * 0.5 }}
                  className="pl-6 border-l-2 border-[#C8A96E]/25"
                >
                  <div className="font-overline text-[#C8A96E] mb-2">{p.position}</div>
                  <NarratedText
                    id={`spread-position-${i}`}
                    text={p.reading}
                    as="p"
                    className="font-heading text-xl text-[#F4EFE6]/90 leading-relaxed"
                    manual
                  />
                </motion.div>
              ))}
            </div>

            {/* Thread */}
            {reading.thread && (
              <motion.div
                data-testid={SPREAD.thread}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6, delay: 1.5 + reading.cards.length * 0.6 + reading.positions.length * 0.5 }}
                className="mt-12 card-frame surface-grain p-8"
              >
                <div className="font-overline text-[#C8A96E] mb-3">The Thread</div>
                <NarratedText
                  id="spread-thread"
                  text={reading.thread}
                  as="p"
                  className="font-heading text-xl sm:text-2xl italic text-[#F4EFE6]/95 leading-relaxed"
                  manual
                />
              </motion.div>
            )}

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <button
                data-testid={SPREAD.saveBtn}
                onClick={saveToJournal}
                disabled={saved}
                className="btn-sacred"
              >
                {saved ? "Kept in journal" : "Keep in journal"}
              </button>
              <button className="btn-sacred" onClick={() => { setReading(null); setSaved(false); }}>
                Draw again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
